import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Users, Search, UserPlus, GraduationCap, BookOpen, Calendar, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FriendDiscovery = () => {
  const { user } = useContext(AuthContext);
  const [discoveredUsers, setDiscoveredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sentRequests, setSentRequests] = useState(new Set());

  useEffect(() => {
    if (user?.profile?.completed_questionnaire) {
      fetchDiscoveredUsers();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchDiscoveredUsers = async () => {
    try {
      const response = await axios.get(`${API}/users/discover`);
      setDiscoveredUsers(response.data.users);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSendFriendRequest = async (userId) => {
    try {
      // Use Supabase context if available, otherwise fallback to placeholder
      if (typeof useSupabase !== 'undefined') {
        const { sendFriendRequest } = useSupabase();
        const result = await sendFriendRequest(userId);
        if (result.success) {
          setSentRequests(prev => new Set([...prev, userId]));
        }
      } else {
        // Fallback for current implementation
        setSentRequests(prev => new Set([...prev, userId]));
        toast.success('Friend request sent! (Feature coming soon)');
      }
    } catch (error) {
      console.error('Failed to send friend request:', error);
      toast.error('Failed to send friend request');
    }
  };

  const filteredUsers = discoveredUsers.filter(discoveredUser => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return true; // Show all if no search
    
    // Search in multiple fields
    const nameMatch = discoveredUser.full_name?.toLowerCase().includes(query);
    const emailMatch = discoveredUser.email?.toLowerCase().includes(query);
    const collegeMatch = discoveredUser.profile?.college_name?.toLowerCase().includes(query);
    const degreeMatch = discoveredUser.profile?.degree_name?.toLowerCase().includes(query);
    const yearMatch = discoveredUser.profile?.study_year?.toLowerCase().includes(query);
    
    return nameMatch || emailMatch || collegeMatch || degreeMatch || yearMatch;
  });

  const getMatchType = (discoveredUser) => {
    if (!user?.profile) return '';
    
    const sameCollege = discoveredUser.profile.college_name === user.profile.college_name;
    const sameDegree = discoveredUser.profile.degree_name === user.profile.degree_name;
    
    if (sameCollege && sameDegree) return 'Same College & Degree';
    if (sameCollege) return 'Same College';
    if (sameDegree) return 'Same Degree';
    return 'Related Field';
  };

  const getMatchColor = (matchType) => {
    switch (matchType) {
      case 'Same College & Degree':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Same College':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Same Degree':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (!user?.profile?.completed_questionnaire) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-cyan-50 p-4 sm:p-6 lg:p-8">
        <div className="max-w-4xl mx-auto">
          <Card className="card text-center py-12">
            <CardContent>
              <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Complete Your Profile First</h2>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                To discover and connect with fellow students, please complete your profile with your academic details.
              </p>
              <Button 
                onClick={() => window.location.reload()}
                className="bg-indigo-500 hover:bg-indigo-600"
                data-testid="complete-profile-cta"
              >
                Complete Profile
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-cyan-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center">
              <Users className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900" data-testid="friends-title">
                Discover Study Partners
              </h1>
              <p className="text-gray-600">Connect with students from your college and field of study</p>
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative max-w-2xl">
            <Search className="h-5 w-5 text-gray-400 absolute left-3 top-3" />
            <Input
              type="text"
              placeholder="Search by name, email, college, degree, or year..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 w-full"
              data-testid="search-friends"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </div>
          
          {/* Search Results Info */}
          {searchQuery && (
            <p className="mt-3 text-sm text-gray-600">
              Found {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''} matching "{searchQuery}"
            </p>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : filteredUsers.length === 0 ? (
          <Card className="card text-center py-12">
            <CardContent>
              <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                {discoveredUsers.length === 0 ? 'No Students Found' : 'No Search Results'}
              </h2>
              <p className="text-gray-600 max-w-md mx-auto">
                {discoveredUsers.length === 0 
                  ? 'Be the first from your college or degree program to join! Invite your friends to connect.'
                  : 'Try adjusting your search terms to find more students.'
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredUsers.map((discoveredUser, index) => {
              const matchType = getMatchType(discoveredUser);
              const matchColor = getMatchColor(matchType);
              const hasRequestSent = sentRequests.has(discoveredUser.id);
              
              return (
                <Card key={discoveredUser.id} className="card hover-lift" data-testid={`user-card-${index}`}>
                  <CardContent className="p-6">
                    {/* Match Type Badge */}
                    <div className="flex justify-end mb-3">
                      <Badge className={`text-xs px-2 py-1 ${matchColor}`}>
                        {matchType}
                      </Badge>
                    </div>

                    {/* User Info */}
                    <div className="text-center mb-4">
                      <Avatar className="w-16 h-16 mx-auto mb-3">
                        <AvatarFallback className="bg-indigo-500 text-white text-lg font-semibold">
                          {discoveredUser.full_name?.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <h3 className="font-semibold text-gray-900 text-lg mb-1">
                        {discoveredUser.full_name}
                      </h3>
                    </div>

                    {/* Academic Details */}
                    <div className="space-y-3 mb-4">
                      <div className="flex items-center space-x-2 text-sm">
                        <MapPin className="h-4 w-4 text-gray-500" />
                        <span className="text-gray-600 truncate">{discoveredUser.profile.college_name}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm">
                        <BookOpen className="h-4 w-4 text-gray-500" />
                        <span className="text-gray-600 truncate">{discoveredUser.profile.degree_name}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm">
                        <Calendar className="h-4 w-4 text-gray-500" />
                        <span className="text-gray-600">{discoveredUser.profile.study_year}</span>
                      </div>
                    </div>

                    {/* Action Button */}
                    <Button
                      onClick={() => handleSendFriendRequest(discoveredUser.id)}
                      disabled={hasRequestSent}
                      className={`w-full ${
                        hasRequestSent 
                          ? 'bg-gray-100 text-gray-500 hover:bg-gray-100' 
                          : 'bg-purple-500 hover:bg-purple-600'
                      }`}
                      data-testid={`friend-request-button-${index}`}
                    >
                      {hasRequestSent ? (
                        <>Request Sent</>
                      ) : (
                        <>
                          <UserPlus className="h-4 w-4 mr-2" />
                          Connect
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Coming Soon Features */}
        <div className="mt-12">
          <Card className="card bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
            <CardContent className="p-6">
              <div className="text-center">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">More Features Coming Soon!</h3>
                <p className="text-gray-600 mb-4">
                  We're working on chat functionality, friend requests, and group study features.
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  <Badge variant="outline">Real-time Chat</Badge>
                  <Badge variant="outline">Friend Requests</Badge>
                  <Badge variant="outline">Study Groups</Badge>
                  <Badge variant="outline">File Sharing</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default FriendDiscovery;